import React, { useEffect, useState } from "react";
import { Spin, Typography, Modal, Alert } from "antd";
import PostalMime from "postal-mime";
import type { Attachment, Email } from "postal-mime";
import {
  FileTextOutlined,
  PaperClipOutlined,
  UserOutlined,
} from "@ant-design/icons";

const { Title } = Typography;

type ProcessedAttachment = Attachment & { objectUrl: string };

type ParsedEmail = Email & { processedAttachments: ProcessedAttachment[] };

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

interface EmlViewerProps {
  blob: Blob;
}

export const EmlViewer: React.FC<EmlViewerProps> = ({ blob }) => {
  const [emailData, setEmailData] = useState<ParsedEmail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const createdUrls: string[] = [];

    const parseEml = async () => {
      setLoading(true);
      setError(null);
      try {
        const parser = new PostalMime();
        const parsed = await parser.parse(blob);

        let htmlContent = parsed.html || "";
        const processedAttachments = (parsed.attachments || []).map((att) => {
          const attBlob = new Blob([att.content], { type: att.mimeType });
          const objUrl = URL.createObjectURL(attBlob);
          createdUrls.push(objUrl);

          if (att.contentId && htmlContent) {
            const cid = att.contentId.replace(/^</, "").replace(/>$/, "");
            // Replace cid: references; the contentId is an arbitrary msg-id token,
            // so escape it before building the pattern.
            const cidRegex = new RegExp(`cid:${escapeRegExp(cid)}`, "gi");
            htmlContent = htmlContent.replace(cidRegex, objUrl);
          }
          return { ...att, objectUrl: objUrl };
        });

        if (isMounted) {
          setEmailData({
            ...parsed,
            html: htmlContent,
            processedAttachments,
          });
        }
      } catch (err) {
        if (isMounted) {
          setError(
            err instanceof Error ? err.message : "Failed to parse EML file",
          );
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    parseEml();
    return () => {
      isMounted = false;
      createdUrls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [blob]);

  if (loading) {
    return (
      <div className="p-8 text-center">
        <Spin />
        <div className="mt-4 text-gray-500">Parsing email contents...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert type="error" message="Error parsing EML" description={error} />
    );
  }

  if (!emailData) return null;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-gray-50 p-4 border-b border-gray-200">
        <Title level={4} className="!mt-0 !mb-4">
          {emailData.subject || "(No Subject)"}
        </Title>
        <div className="grid grid-cols-[80px_1fr] gap-y-2 text-sm">
          <div className="text-gray-500 font-medium">From:</div>
          <div>
            <UserOutlined className="mr-1 text-gray-400" />
            <span className="font-semibold">{emailData.from?.name}</span>{" "}
            <span className="text-gray-500">
              &lt;{emailData.from?.address}&gt;
            </span>
          </div>

          <div className="text-gray-500 font-medium">To:</div>
          <div>
            {(emailData.to ?? []).map((t, i, recipients) => (
              <span key={t.address ?? t.name ?? i} className="mr-2">
                {t.name}{" "}
                <span className="text-gray-500">&lt;{t.address}&gt;</span>
                {i < recipients.length - 1 ? "," : ""}
              </span>
            ))}
          </div>

          {emailData.date && (
            <>
              <div className="text-gray-500 font-medium">Date:</div>
              <div>{new Date(emailData.date).toLocaleString()}</div>
            </>
          )}
        </div>
      </div>

      {/* Attachments */}
      {emailData.processedAttachments.length > 0 && (
        <div className="p-3 bg-gray-100 border-b border-gray-200 flex flex-wrap gap-2 items-center">
          <PaperClipOutlined className="text-gray-500" />
          <span className="text-sm text-gray-500 mr-2">
            {emailData.processedAttachments.length} Attachment(s):
          </span>
          {emailData.processedAttachments.map((att, i) => {
            return (
              <a
                key={att.objectUrl}
                href={att.objectUrl}
                download={att.filename || `attachment-${i}`}
                className="bg-white border border-gray-300 rounded px-2 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 transition-colors"
              >
                {att.filename || "Unnamed attachment"}
              </a>
            );
          })}
        </div>
      )}

      {/* Body */}
      <div className="p-5 flex-1 overflow-auto bg-white">
        {emailData.html ? (
          <iframe
            srcDoc={emailData.html}
            title="Email content"
            className="w-full h-full border-0 min-h-[400px]"
            sandbox="allow-same-origin allow-popups"
          />
        ) : (
          <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800">
            {emailData.text || "(No content)"}
          </pre>
        )}
      </div>
    </div>
  );
};

interface EmlViewerModalProps {
  blob: Blob;
  filename: string;
  visible: boolean;
  onClose: () => void;
}

export const EmlViewerModal: React.FC<EmlViewerModalProps> = ({
  blob,
  filename,
  visible,
  onClose,
}) => {
  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      title={
        <div className="flex items-center gap-2">
          <FileTextOutlined className="text-blue-500" />
          <span>{filename}</span>
        </div>
      }
      width={900}
      styles={{ body: { padding: 0, height: "70vh" } }}
      destroyOnHidden
    >
      <EmlViewer blob={blob} />
    </Modal>
  );
};
